/**
 * Tipos y catálogos de los gastos de mantenimiento.
 *
 * Los documentos son los de `sereno_lavanderia/maintenance_expenses_v2`, que es
 * de la app de lavandería; el CRM es una segunda ventana sobre el mismo
 * contenedor. Esta copia del tipo no se importa de allí: son dos repos y dos
 * despliegues. Si la lavandería cambia estos campos, el cambio se trae a mano
 * aquí y a `server/src/routes/gastos.ts`.
 */

export type EstadoGasto = 'PENDIENTE' | 'COBRADO' | 'deleted';

export interface Gasto {
  id: string;
  /** Id del listing en Hostify, que es la clave de partición del contenedor. */
  propertyId: string;
  /** Nombre congelado el día del alta; se enseña tal cual para no perder el histórico. */
  propertyName: string;
  amount: number;
  /**
   * Lo que se le repercutió de verdad al propietario, que no tiene por qué
   * coincidir con lo que costó. Es 0 mientras el gasto está pendiente.
   */
  chargedAmount: number;
  concept: string;
  observations?: string | null;
  /** `YYYY-MM-DD`. */
  expenseDate: string;
  category?: string | null;
  status: EstadoGasto;
  createdAt?: string | null;
  createdBy?: string | null;
  updatedAt?: string | null;
}

/** Una propiedad del selector: id de Hostify y su nombre actual en el PMS. */
export interface PropiedadGasto {
  id: string;
  nickname: string;
}

export interface ListadoGastos {
  gastos: Gasto[];
  /** Cuántos hay antes de recortar por `limit`, para avisar si falta alguno. */
  total: number;
}

export interface DatosGasto {
  propertyId: string;
  propertyName: string;
  amount: number;
  concept: string;
  observations?: string;
  expenseDate: string;
  category?: string;
  status?: EstadoGasto;
}

/**
 * Las categorías del alta en la app de lavandería, que son las que su listado
 * sabe pintar.
 *
 * Allí el modal de edición ofrece otras diez (Electricidad, Agua, Gas…) que no
 * coinciden con ninguna de estas: al editar un gasto de «Reparación» el
 * desplegable salía vacío y guardarlo le borraba la categoría. Aquí el alta y la
 * edición comparten esta lista, y `opcionesCategoria` añade la que traiga el
 * documento si es de las viejas, para que editarlo no la pierda.
 */
export const CATEGORIAS = ['Reparación', 'Compra', 'Extra limpieza', 'Otro'] as const;

export function opcionesCategoria(actual?: string | null): string[] {
  const lista: string[] = [...CATEGORIAS];
  const valor = (actual || '').trim();
  if (valor && !lista.includes(valor)) lista.push(valor);
  return lista;
}

export const ETIQUETAS_ESTADO: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  COBRADO: 'Cobrado',
};

/** Hoy en horario de Madrid, en `YYYY-MM-DD`, para el valor por defecto de la fecha. */
export function hoyISO(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' });
}

/**
 * Importe en euros con dos decimales.
 *
 * No usa `formatCurrency` de `lib/utils` porque aquélla redondea a enteros
 * (`minimumFractionDigits: 0`), y un gasto de 12,40 € se leería como 12 €.
 */
export function formatImporte(importe: number): string {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(importe);
}
